import React, { FC, memo } from 'react';
import { useSelector } from 'react-redux';
import { OpenCampaignCardAspectRatio } from '@browsing/campaign';

import { Campaign } from '@shared/types/mylounge';
import { getCategoryTabId } from '@client/redux/modules/mylounge';
import { CategoryTabIds } from '@shared/constants';

import { useResponsiveColumns } from './helpers';
import { Container, GridWrapper, StyledOpenCampaignCard } from './styles';
import { OneColumnLayout, TwoColumnLayout, ThreeColumnLayout, FourColumnLayout } from './layout';
import { GridLayoutProvider } from './context/GridLayoutContext';
import { useGridLayoutContextValue } from './context/useGridLayoutContextValue';

interface Props {
    campaigns: Campaign[];
    isPlusEnabled: boolean;
}

export const Grid: FC<Props> = ({ campaigns, isPlusEnabled }) => {
    const categoryTabId = useSelector(getCategoryTabId);

    // Use responsive columns hook for layout selection
    const columnCount = useResponsiveColumns();

    const gridLayoutContextValue = useGridLayoutContextValue({
        campaigns,
        isPlusEnabled,
        columnCount,
    });

    if (!campaigns) {
        return null;
    }

    // Simple grid for non-ALL categories
    if (categoryTabId !== CategoryTabIds.ALL) {
        return (
            <Container>
                <GridWrapper columnCount={columnCount} id="campaigns-open">
                    {campaigns.map((campaign, index) => (
                        <StyledOpenCampaignCard
                            key={campaign.campaignId}
                            $colSpan={1}
                            $rowSpan={1}
                            aspectRatio={
                                columnCount === 1
                                    ? OpenCampaignCardAspectRatio.Mobile
                                    : OpenCampaignCardAspectRatio.Square
                            }
                            campaign={campaign}
                            isPlusEnabled={isPlusEnabled}
                            position={`${index + 1}|${columnCount}`}
                        />
                    ))}
                </GridWrapper>
            </Container>
        );
    }

    // Single column layout for mobile in all tabs
    if (columnCount === 1) {
        return (
            <GridLayoutProvider value={gridLayoutContextValue}>
                <OneColumnLayout />
            </GridLayoutProvider>
        );
    }

    if (columnCount === 2) {
        return (
            <GridLayoutProvider value={gridLayoutContextValue}>
                <TwoColumnLayout />
            </GridLayoutProvider>
        );
    }

    if (columnCount === 3) {
        return (
            <GridLayoutProvider value={gridLayoutContextValue}>
                <ThreeColumnLayout />
            </GridLayoutProvider>
        );
    }

    // 4 columns
    return (
        <GridLayoutProvider value={gridLayoutContextValue}>
            <FourColumnLayout />
        </GridLayoutProvider>
    );
};

export default memo(Grid);
